/**
 * Subscription Price Updater
 * Dynamically updates elements when user changes subscription plan:
 * - .js-price-now = total price to pay now
 * - .js-price-per-box = price per box × total quantities from properties[... Quantity]
 * - .js-row-per-box = price per box × total quantities (inside subscription rows)
 * - .js-row-was = the row's data-was (bought one at a time) × total quantities
 * - .js-row-saving = data-was − data-price-now × total quantities, in money
 * - .js-row-price-now = price now × total quantities (inside subscription rows)
 * - .js-cadence = delivery cadence (e.g. "2 weeks", "month")
 * - [data-message-text] = text with placeholders [PRICE] and [FREQUENCY]
 */

(function() {
  'use strict';

  // Function to format prices
  const money = (cents) => {
    try {
      if (Shopify?.formatMoney && Shopify?.money_format) {
        return Shopify.formatMoney(Math.round(cents), Shopify.money_format);
      }
    } catch (e) {
      console.warn('[Price Updater] Shopify formatMoney not available:', e);
    }
    // Fallback
    return (Math.round(cents) / 100).toFixed(2);
  };

  // "Save", from the widget's locale block - this file has no i18n of its own
  let SAVE_LABEL = 'Save';
  try {
    const i18nEl = document.querySelector('.js-widget-i18n');
    if (i18nEl) SAVE_LABEL = JSON.parse(i18nEl.textContent).save || SAVE_LABEL;
  } catch (e) { /* keep the fallback */ }

  // Helper function to get total quantity from a section
  function getTotalQuantity(section) {
    if (!section) return 1;
    
    let totalQuantity = 0;
    
    // 1. First, check for properties[... Quantity] inputs and sum them
    const quantityInputs = section.querySelectorAll('input[name^="properties["][name$=" Quantity]"]');
    if (quantityInputs.length > 0) {
      quantityInputs.forEach(input => {
        totalQuantity += parseInt(input.value) || 0;
      });
    }
    
    // 2. If no properties quantities found, check for main quantity input
    if (totalQuantity === 0) {
      const mainQuantityInput = section.querySelector('input[name="quantity"]');
      if (mainQuantityInput) {
        totalQuantity = parseInt(mainQuantityInput.value) || 0;
      }
    }
    
    // 3. Ensure minimum of 1
    return totalQuantity || 1;
  }

  // Update all elements
  function updateAllPrices() {
    const container = document.querySelector('[id^="SubscriptionPlans-"]');
    if (!container) return;

    const selectedRadio = container.querySelector('input[name="selling_plan_id"]:checked');
    
    let priceNow = null;
    let pricePerBox = null;
    let cadence = null;
    let deliveries = null;

    if (selectedRadio) {
      // Plan selected - use subscription data
      const selectedRow = selectedRadio.closest('.subscription-plan-row');
      if (selectedRow) {
        priceNow = selectedRow.getAttribute('data-price-now');
        pricePerBox = selectedRow.getAttribute('data-per-box');
        cadence = selectedRow.getAttribute('data-cadence');
        deliveries = selectedRow.getAttribute('data-deliveries');
      }
    } else {
      // No plan selected - use base price (buy once)
      const onetimeRow = container.querySelector('.onetime-info-row');
      if (onetimeRow) {
        priceNow = onetimeRow.getAttribute('data-base-price');
        pricePerBox = priceNow; // For buy once, price per box = total price
        cadence = null; // Buy once has no cadence
        deliveries = null;
      }
    }

    // Update .js-price-now elements (total price)
    if (priceNow) {
      const formattedPriceNow = money(parseInt(priceNow));
      document.querySelectorAll('.js-price-now').forEach(el => {
        el.textContent = formattedPriceNow;
      });
    }

    // Update .js-price-per-box elements (price per box × total quantities)
    if (pricePerBox) {
      const pricePerBoxCents = parseInt(pricePerBox);
      
      document.querySelectorAll('.js-price-per-box').forEach(el => {
        const section = el.closest('section');
        const totalQuantity = getTotalQuantity(section);
        const totalPrice = pricePerBoxCents * totalQuantity;
        el.textContent = money(totalPrice);
      });
    }

    // Update .js-row-per-box elements (each row uses its own data-per-box value)
    document.querySelectorAll('.js-row-per-box').forEach(el => {
      // Find the closest subscription-plan-row to get this row's own data-per-box
      const row = el.closest('.subscription-plan-row');
      if (!row) return;
      
      const rowPricePerBox = row.getAttribute('data-per-box');
      if (!rowPricePerBox) return;
      
      const pricePerBoxCents = parseInt(rowPricePerBox);
      const section = el.closest('section');
      const totalQuantity = getTotalQuantity(section);
      const totalPrice = pricePerBoxCents * totalQuantity;
      el.textContent = money(totalPrice);
    });

    // Update .js-row-was / .js-row-saving (the struck "bought one at a time"
    // total and the euros kept). data-was is worked out by fillRow() in
    // snippets/recharge-custom-widget.liquid, which is the only place that
    // knows the variant->plan price map; 0 means this row has no saving.
    document.querySelectorAll('.js-row-was, .js-row-saving').forEach(el => {
      const row = el.closest('.subscription-plan-row');
      if (!row) return;

      const was = parseInt(row.getAttribute('data-was')) || 0;
      const priceNow = parseInt(row.getAttribute('data-price-now')) || 0;
      if (was <= priceNow) { el.textContent = ''; return; }

      const totalQuantity = getTotalQuantity(el.closest('section'));
      el.textContent = el.classList.contains('js-row-was')
        ? money(was * totalQuantity)
        : `${SAVE_LABEL} ${money((was - priceNow) * totalQuantity)}`;
    });

    // Update .js-row-price-now elements (each row uses its own data-price-now value)
    document.querySelectorAll('.js-row-price-now').forEach(el => {
      // Find the closest subscription-plan-row to get this row's own data-price-now
      const row = el.closest('.subscription-plan-row');
      if (!row) return;
      
      const rowPriceNow = row.getAttribute('data-price-now');
      if (!rowPriceNow) return;
      
      const priceNowCents = parseInt(rowPriceNow);
      const section = el.closest('section');
      const totalQuantity = getTotalQuantity(section);
      const totalPrice = priceNowCents * totalQuantity;
      el.textContent = money(totalPrice);
    });

    // Update .js-onetime-per-box and .js-onetime-total elements (one-time purchase prices × quantity)
    const onetimeRow = document.querySelector('.onetime-info-row');
    if (onetimeRow) {
      const basePrice = parseInt(onetimeRow.getAttribute('data-base-price')) || 0;
      const section = onetimeRow.closest('section');
      const totalQuantity = getTotalQuantity(section);
      const totalOnetimePrice = basePrice * totalQuantity;
      
      // Update data-per-box attribute for consistency
      onetimeRow.setAttribute('data-per-box', totalOnetimePrice);
      
      // Update .js-onetime-per-box elements
      document.querySelectorAll('.js-onetime-per-box').forEach(el => {
        el.textContent = money(totalOnetimePrice);
      });
      
      // Update .js-onetime-total elements
      document.querySelectorAll('.js-onetime-total').forEach(el => {
        el.textContent = money(totalOnetimePrice);
      });
    }

    // Update .js-cadence elements (cadence)
    document.querySelectorAll('.js-cadence').forEach(el => {
      el.textContent = cadence ? `/ ${cadence}` : '';
    });

    // Update elements with data-message-text (messages with placeholders)
    document.querySelectorAll('[data-message-text]').forEach(el => {
      // If buy once (one-time), hide the text
      if (!deliveries || !cadence) {
        el.style.display = 'none';
        return;
      }
      
      // Show element if hidden
      el.style.display = '';
      
      // Calculate total price: priceNow × total quantities
      let totalPrice = priceNow ? parseInt(priceNow) : 0;
      
      if (priceNow) {
        const section = el.closest('section');
        const totalQuantity = getTotalQuantity(section);
        totalPrice = parseInt(priceNow) * totalQuantity;
      }
      
      // Decode HTML entities from data attribute
      const textarea = document.createElement('textarea');
      textarea.innerHTML = el.dataset.messageText;
      let text = textarea.value;
      
      // Replace [PRICE] and [FREQUENCY] in a single span.bold
      const formattedPrice = money(totalPrice);
      const frequency = `${deliveries} ${cadence}${deliveries > 1 ? 's' : ''}`;
      
      // Capture everything between [PRICE] and [FREQUENCY] and wrap in single span
      text = text.replace(/\[PRICE\](.*?)\[FREQUENCY\]/g, (match, middle) => {
        return `<span class="bold">${formattedPrice}${middle}${frequency}</span>`;
      });
      
      // Use innerHTML to preserve HTML tags (rich text)
      el.innerHTML = text;
    });
  }

  // Listen to changes in subscription radio buttons
  function setupListeners() {
    const container = document.querySelector('[id^="SubscriptionPlans-"]');
    if (!container) return; // No subscription container on page

    // Specific listener on container for radio button changes
    container.addEventListener('change', (e) => {
      if (e.target.name === 'selling_plan_id') {
        updateAllPrices();
      }
    });

    // Specific listener on container for "Buy Once" option click
    container.addEventListener('click', (e) => {
      if (e.target.closest('.onetime-info-row')) {
        setTimeout(updateAllPrices, 50);
      }
    });

    // Listen for changes on quantity property inputs (properties[... Quantity])
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name^="properties["][name$=" Quantity]"]')) {
        updateAllPrices();
      }
    });

    // Variant change (Shopify pub/sub - for themes that use it)
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      subscribe(window.PUB_SUB_EVENTS.variantChange, () => {
        updateAllPrices();
      });
    }
  }

  // Callback for when Recharge data is ready
  window.updateSubscriptionPrices = function() {
    updateAllPrices();
  };

  // Setup listeners when DOM loads
  document.addEventListener('DOMContentLoaded', () => {
    setupListeners();
  });
})();


